import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, forkJoin, lastValueFrom, Observable, of } from 'rxjs';
import { environment } from '@env/environment';
import {
    DebugFeature,
    DEFAULT_DEBUG_FEATURES,
} from '../components/debug-command-window/debug';
import { catchError, map, tap } from 'rxjs/operators';
import { UsersService } from 'api/services/users.service';
import { DeliveryNoteBundlesService } from 'api/services/delivery-note-bundles.service';
import { DeliveryNotesService } from '@app/core/services/backend-services/delivery-notes-service/delivery-notes.service';
import { HttpEvent } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { SelectedActors } from '../components/chain-selection-dialog/chain-selection-dialog.component';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';
import { ArticlesService } from 'api/addons-v3/services/articles.service';
import { AdvancedShippingNoticeService } from 'api/addons-v3/services/advanced-shipping-notice.service';
import {
    DebugArticleCompareDialogComponent,
    DebugArticleCompareInputData,
    DebugArticleCompareResultData,
} from '@app/shared/components/debug-command-window/debug-article-compare-dialog/debug-article-compare-dialog.component';
import {
    DebugAsnUploadDialogComponent,
    DebugAsnUploadInputData,
    DebugAsnUploadResultData,
} from '@app/shared/components/debug-command-window/debug-asn-upload-dialog/debug-asn-upload-dialog.component';
import {
    DebugManageArticlesDialogComponent,
    DebugManageArticlesInputData,
} from '@app/shared/components/debug-command-window/debug-manage-articles-dialog/debug-manage-articles-dialog.component';

export interface DebugState {
    environment: 'production' | 'test';
    loggingEnabled: boolean;
    customFeatures: Record<string, boolean>;
    visible?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class DebugService {
    private readonly STORAGE_KEY = 'debugState';
    private readonly POSITION_KEY = 'debugButtonPosition';
    readonly loading = signal(false);
    private initialState: DebugState = {
        environment: environment.production ? 'production' : 'test',
        loggingEnabled: false,
        customFeatures: {},
        visible: !environment.production,
    };

    private debugStateSubject = new BehaviorSubject<DebugState>(
        this.initialState
    );
    private featuresSubject = new BehaviorSubject<DebugFeature[]>(
        DEFAULT_DEBUG_FEATURES
    );

    constructor(
        private usersService: UsersService,
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private deliveryNotesService: DeliveryNotesService,
        private dialog: MatDialog,
        private sessionService: SessionService,
        private articlesService: ArticlesService,
        private advancedShippingNoticeService: AdvancedShippingNoticeService
    ) {
        this.loadStateFromStorage();
    }

    private loadStateFromStorage(): void {
        try {
            const savedState = localStorage.getItem(this.STORAGE_KEY);
            if (savedState) {
                const parsedState = JSON.parse(
                    savedState
                ) as Partial<DebugState>;
                if (!parsedState.customFeatures)
                    parsedState.customFeatures = {};

                const effectiveEnvironment =
                    parsedState.environment ?? this.initialState.environment;

                const isVisible = environment.production
                    ? (parsedState.visible ?? false)
                    : true;

                const mergedState: DebugState = {
                    ...this.initialState,
                    ...parsedState,
                    environment: effectiveEnvironment,
                    visible: isVisible,
                };

                this.debugStateSubject.next(mergedState);

                this.updateDefaultFeaturesFromState(mergedState);

                if (mergedState.loggingEnabled) {
                    console.log(
                        '[DebugService] Debug state loaded:',
                        mergedState
                    );
                }
            } else {
                this.updateDefaultFeaturesFromState(this.initialState);
            }
        } catch (error) {
            this.debugStateSubject.next({ ...this.initialState });
            this.featuresSubject.next([...DEFAULT_DEBUG_FEATURES]);
            this.updateDefaultFeaturesFromState(this.initialState);
        }
    }

    private updateDefaultFeaturesFromState(state: DebugState): void {
        const updatedFeatures = DEFAULT_DEBUG_FEATURES.map(feature => ({
            ...feature,
        }));

        const envFeature = updatedFeatures.find(f => f.id === 'environment');
        if (envFeature) {
            envFeature.enabled = state.environment === 'test';
        }

        const loggingFeature = updatedFeatures.find(f => f.id === 'logging');
        if (loggingFeature) {
            loggingFeature.enabled = state.loggingEnabled;
        }

        this.featuresSubject.next(updatedFeatures);
    }

    private saveStateToStorage(): void {
        try {
            localStorage.setItem(
                this.STORAGE_KEY,
                JSON.stringify(this.debugStateSubject.value)
            );
        } catch (error) {}
    }

    getDebugState(): Observable<DebugState> {
        return this.debugStateSubject.asObservable();
    }

    getCurrentDebugState(): DebugState {
        return this.debugStateSubject.value;
    }

    isDebugVisible(): boolean {
        return this.debugStateSubject.value.visible === true;
    }

    hideDebug(): void {
        const currentState = { ...this.debugStateSubject.value };
        currentState.visible = false;
        this.debugStateSubject.next(currentState);
        this.saveStateToStorage();

        if (currentState.loggingEnabled) {
            console.log('[DebugService] Debug UI hidden until page reload');
        }
    }

    showDebug(): void {
        const currentState = { ...this.debugStateSubject.value };
        currentState.visible = true;
        this.debugStateSubject.next(currentState);
        this.saveStateToStorage();

        if (currentState.loggingEnabled) {
            console.log('[DebugService] Debug UI shown');
        }
    }

    updateFeature(featureId: string, enabled: boolean): void {
        const currentState = { ...this.debugStateSubject.value };
        const currentFeatures = [...this.featuresSubject.value];
        const featureIndex = currentFeatures.findIndex(f => f.id === featureId);
        let needsReload = false;

        switch (featureId) {
            case 'environment':
                const newEnv = enabled ? 'test' : 'production';
                if (currentState.environment !== newEnv) {
                    currentState.environment = newEnv;
                    if (featureIndex !== -1)
                        currentFeatures[featureIndex].enabled = enabled;
                    needsReload = true;

                    if (newEnv === 'production') {
                        currentState.visible = false;
                    }

                    if (currentState.loggingEnabled || newEnv === 'test') {
                        console.log(
                            `[DebugService] Setting environment to '${newEnv}'. Reloading application...`
                        );
                    }
                }
                break;

            case 'logging':
                currentState.loggingEnabled = enabled;
                if (featureIndex !== -1)
                    currentFeatures[featureIndex].enabled = enabled;
                if (enabled) {
                    console.log(
                        `[DebugService] Feature '${featureId}' updated:`,
                        enabled
                    );
                } else {
                    console.log(
                        `[DebugService] Feature '${featureId}' updated:`,
                        enabled,
                        '(Disabling logging)'
                    );
                }
                break;

            default:
                if (
                    featureIndex !== -1 &&
                    currentFeatures[featureIndex].type === 'toggle'
                ) {
                    currentFeatures[featureIndex].enabled = enabled;
                    currentState.customFeatures[featureId] = enabled;
                    if (currentState.loggingEnabled) {
                        console.log(
                            `[DebugService] Feature '${featureId}' updated:`,
                            enabled
                        );
                    }
                } else {
                    if (currentState.loggingEnabled) {
                        console.warn(
                            `[DebugService] Attempted to update non-toggle or unknown feature UI: ${featureId}`
                        );
                    }
                }
                break;
        }

        this.debugStateSubject.next(currentState);
        this.featuresSubject.next(currentFeatures);

        this.saveStateToStorage();

        if (needsReload) {
            setTimeout(() => window.location.reload(), 100);
        }
    }

    executeAction(actionId: string): void {
        const currentState = this.getCurrentDebugState();
        const feature = this.featuresSubject.value.find(f => f.id === actionId);

        if (currentState.loggingEnabled) {
            console.log(
                `[DebugService] Executing action: ${actionId} (${feature?.name || 'Unknown Action'})`
            );
        }

        switch (actionId) {
            case 'clearStorage':
                this.clearStorage();
                alert(
                    'Storage cleared (preserving debug settings & session ID)'
                );
                break;
            case 'reloadApp':
                if (currentState.loggingEnabled)
                    console.log(
                        '[DebugService] Reloading application via action...'
                    );
                setTimeout(() => window.location.reload(), 100);
                break;
            case 'clearCache':
                this.clearCache();
                break;
            case 'createBundle':
                this.createBundle();
                break;
            case 'hideDebug':
                this.hideDebug();
                break;
            case 'compareArticle':
                this.compareArticle();
                break;
            case 'updateAsn':
                this.updateAsn();
                break;
            case 'manageArticles':
                this.manageArticles();
                break;
            default:
                if (currentState.loggingEnabled)
                    console.warn(
                        `[DebugService] Action '${actionId}' not implemented.`
                    );
                if (feature) alert(`Action '${feature.name}' not implemented.`);
        }
    }

    clearCache(): void {
        const currentState = this.getCurrentDebugState();

        if (currentState.loggingEnabled) {
        }

        if ('caches' in window) {
            window.caches.keys().then(cacheNames => {
                cacheNames.forEach(cacheName => {
                    window.caches.delete(cacheName).then(() => {
                        if (currentState.loggingEnabled) {
                            console.log(
                                `[DebugService] Cache ${cacheName} cleared`
                            );
                        }
                    });
                });

                alert('Browser cache cleared. Reloading page...');

                setTimeout(() => {
                    window.location.reload();

                    const currentUrl = (window as Window).location.href;
                    const timestamp = new Date().getTime();
                    const separator = currentUrl.includes('?') ? '&' : '?';
                    window.location.href = `${currentUrl}${separator}nocache=${timestamp}`;
                }, 100);
            });
        } else {
            alert('Attempting to reload without cache...');
            const currentUrl = (window as Window).location.href;
            const timestamp = new Date().getTime();
            const separator = currentUrl.includes('?') ? '&' : '?';
            (window as Window).location.href =
                `${currentUrl}${separator}nocache=${timestamp}`;
        }
    }

    clearStorage(): void {
        const sessionId = localStorage.getItem('sessionId');
        const debugState = localStorage.getItem(this.STORAGE_KEY);
        const debugButtonPosition = localStorage.getItem(this.POSITION_KEY);

        const wasLoggingEnabled = this.getCurrentDebugState().loggingEnabled;

        localStorage.clear();
        sessionStorage.clear();

        if (sessionId) localStorage.setItem('sessionId', sessionId);
        if (debugState) localStorage.setItem(this.STORAGE_KEY, debugState);
        if (debugButtonPosition)
            localStorage.setItem(this.POSITION_KEY, debugButtonPosition);

        if (wasLoggingEnabled) {
            console.log(
                '[DebugService] Storage cleared (preserving debug settings & session ID)'
            );
        }
    }

    async createBundle(): Promise<void> {
        this.loading.set(true);
        let organizationSiteKey: string | undefined;
        let carrierKey: string | undefined;
        let consigneeKey: string | undefined;
        let consignorKey: string | undefined;
        let createdBundleKey: string | undefined;
        let finalBundleName: string | undefined;
        let bundleSummaryMessage: string = '';

        let consignorName: string | undefined;
        let carrierName: string | undefined;
        let consigneeName: string | undefined;

        try {
            const sites = await lastValueFrom(
                this.usersService.getAccessibleUserSites()
            );

            const actors = await lastValueFrom(
                this.usersService.getMeSupplyChains()
            );

            const useManualSelection = confirm(
                'Do you want to manually select carrier, consignee, and consignor?\n\n' +
                    "Click 'OK' to select manually.\n" +
                    "Click 'Cancel' to use automatic selection."
            );

            if (useManualSelection) {
                if (
                    !actors ||
                    !actors.carrier ||
                    actors.carrier.length === 0 ||
                    !actors.consignee ||
                    actors.consignee.length === 0 ||
                    !actors.consignor ||
                    actors.consignor.length === 0
                ) {
                    alert(
                        'Cannot perform manual selection: Unable to retrieve a complete list of actors.'
                    );
                } else {
                    try {
                        const selectedActors =
                            await this.openActorSelectionDialog(actors);

                        if (
                            selectedActors &&
                            selectedActors.consignor &&
                            selectedActors.carrier &&
                            selectedActors.consignee
                        ) {
                            consignorKey = selectedActors.consignor.key;
                            consignorName = selectedActors.consignor.name;
                            carrierKey = selectedActors.carrier.key;
                            carrierName = selectedActors.carrier.name;
                            consigneeKey = selectedActors.consignee.key;
                            consigneeName = selectedActors.consignee.name;

                            organizationSiteKey = consignorKey;

                            if (this.isLoggingEnabled()) {
                                console.log(
                                    '[DebugService] Manual selection complete:',
                                    {
                                        consignor: {
                                            key: consignorKey,
                                            name: consignorName,
                                        },
                                        carrier: {
                                            key: carrierKey,
                                            name: carrierName,
                                        },
                                        consignee: {
                                            key: consigneeKey,
                                            name: consigneeName,
                                        },
                                    }
                                );
                            }
                        } else {
                            if (this.isLoggingEnabled()) {
                                console.log(
                                    '[DebugService] Manual selection cancelled or incomplete'
                                );
                            }

                            alert(
                                'Manual selection was incomplete. Falling back to automatic selection.'
                            );

                            carrierKey = undefined;
                            consigneeKey = undefined;
                            consignorKey = undefined;
                            organizationSiteKey = undefined;
                            consignorName = undefined;
                            carrierName = undefined;
                            consigneeName = undefined;
                        }
                    } catch (dialogError: any) {
                        console.error(
                            'Error in dialog selection:',
                            dialogError
                        );
                        alert(
                            `Error during manual selection: ${dialogError.message || 'Unknown error'}\nFalling back to automatic selection.`
                        );

                        carrierKey = undefined;
                        consigneeKey = undefined;
                        consignorKey = undefined;
                        organizationSiteKey = undefined;
                        consignorName = undefined;
                        carrierName = undefined;
                        consigneeName = undefined;
                    }
                }
            }

            if (
                !carrierKey ||
                !consigneeKey ||
                !consignorKey ||
                !organizationSiteKey
            ) {
                // Helper function to check if a site has the required role
                const hasSiteValidRole = (
                    site: any,
                    roleName: string
                ): boolean => {
                    return (
                        site.roles &&
                        Array.isArray(site.roles) &&
                        site.roles.includes(roleName)
                    );
                };

                // Check for site with all roles and valid role assignments
                const siteWithAllRoles = sites.find(
                    (site: any) =>
                        site.carrier === true &&
                        site.consignee === true &&
                        site.consignor === true &&
                        hasSiteValidRole(site, 'carrier') &&
                        hasSiteValidRole(site, 'consignee') &&
                        hasSiteValidRole(site, 'consignor')
                );

                if (siteWithAllRoles) {
                    // Site has all required flags and roles
                    organizationSiteKey = siteWithAllRoles._key;
                    carrierKey = siteWithAllRoles._key;
                    consigneeKey = siteWithAllRoles._key;
                    consignorKey = siteWithAllRoles._key;
                    consignorName = siteWithAllRoles.name;
                    carrierName = siteWithAllRoles.name;
                    consigneeName = siteWithAllRoles.name;

                    if (this.isLoggingEnabled()) {
                        console.log(
                            '[DebugService] Using site with all roles:',
                            siteWithAllRoles
                        );
                    }
                } else {
                    // Find individual sites with the correct flag AND role combination
                    const carrierSite = sites.find(
                        (site: any) =>
                            site.carrier === true &&
                            hasSiteValidRole(site, 'carrier')
                    );

                    const consigneeSite = sites.find(
                        (site: any) =>
                            site.consignee === true &&
                            hasSiteValidRole(site, 'consignee')
                    );

                    const consignorSite = sites.find(
                        (site: any) =>
                            site.consignor === true &&
                            hasSiteValidRole(site, 'consignor')
                    );

                    if (carrierSite && consigneeSite && consignorSite) {
                        // Use individual sites with correct roles
                        organizationSiteKey = consignorSite._key;
                        carrierKey = carrierSite._key;
                        consigneeKey = consigneeSite._key;
                        consignorKey = consignorSite._key;
                        consignorName = consignorSite.name;
                        carrierName = carrierSite.name;
                        consigneeName = consigneeSite.name;

                        if (this.isLoggingEnabled()) {
                            console.log(
                                '[DebugService] Using separate sites with correct roles:',
                                {
                                    consignor: consignorSite,
                                    carrier: carrierSite,
                                    consignee: consigneeSite,
                                }
                            );
                        }
                    } else {
                        // If no site matches with both flags and roles, try using actors from supply chain
                        const validateActorRole = (
                            actor: any,
                            roleName: string
                        ): boolean => {
                            return (
                                actor.roles &&
                                Array.isArray(actor.roles) &&
                                actor.roles.includes(roleName)
                            );
                        };

                        if (
                            actors.carrier.length > 0 &&
                            actors.consignee.length > 0 &&
                            actors.consignor.length > 0
                        ) {
                            // Find actors with correct roles
                            const validConsignorActor =
                                actors.consignor.find((actor: any) =>
                                    validateActorRole(actor, 'consignor')
                                ) || actors.consignor[0];

                            const validCarrierActor =
                                actors.carrier.find((actor: any) =>
                                    validateActorRole(actor, 'carrier')
                                ) || actors.carrier[0];

                            const validConsigneeActor =
                                actors.consignee.find((actor: any) =>
                                    validateActorRole(actor, 'consignee')
                                ) || actors.consignee[0];

                            organizationSiteKey = validConsignorActor._key;
                            carrierKey = validCarrierActor._key;
                            consigneeKey = validConsigneeActor._key;
                            consignorKey = validConsignorActor._key;
                            consignorName = validConsignorActor.name;
                            carrierName = validCarrierActor.name;
                            consigneeName = validConsigneeActor.name;

                            if (this.isLoggingEnabled()) {
                                console.log(
                                    '[DebugService] Using actors from supply chain:',
                                    {
                                        consignor: validConsignorActor,
                                        carrier: validCarrierActor,
                                        consignee: validConsigneeActor,
                                    }
                                );
                            }
                        } else {
                            this.loading.set(false);
                            alert(
                                'Error: Could not find suitable actors (consignor, carrier, consignee) with correct roles in accessible sites or supply chains for bundle creation.'
                            );
                            return;
                        }
                    }
                }
            }

            if (
                !organizationSiteKey ||
                !carrierKey ||
                !consigneeKey ||
                !consignorKey
            ) {
                this.loading.set(false);
                alert(
                    'Error: Could not determine all required keys for bundle creation.'
                );
                return;
            }

            const promptMessage =
                'Please enter a name for the bundle (optional):';
            const defaultBundleName = `Bundle ${new Date().toISOString()}`;
            const bundleNameInput = prompt(promptMessage, defaultBundleName);

            if (bundleNameInput === null) {
                this.loading.set(false);
                return;
            } else if (bundleNameInput.trim() !== '') {
                finalBundleName = bundleNameInput.trim();
            } else {
                finalBundleName = defaultBundleName;
            }

            const createdBundle = await lastValueFrom(
                this.deliveryNoteBundlesService.postNewBundle({
                    organizationSiteKey: organizationSiteKey,
                    body: {
                        name: finalBundleName,
                        carrierKey: carrierKey,
                        consigneeKey: consigneeKey,
                        consignorKey: consignorKey,
                        deliveryNoteKeys: [],
                    },
                })
            );

            if (!createdBundle || !createdBundle._key) {
                this.loading.set(false);
                alert(
                    'Error: Bundle creation API call failed or did not return a valid bundle.'
                );
                return;
            }

            createdBundleKey = createdBundle._key;
            bundleSummaryMessage = `Bundle "${finalBundleName}" created (${createdBundleKey}).\nConsignor: ${consignorName}\nCarrier: ${carrierName}\nConsignee: ${consigneeName}\n\n`;

            await this.selectAndUploadPdfs(
                organizationSiteKey,
                createdBundleKey,
                bundleSummaryMessage
            );
        } catch (error: any) {
            const errorMsg =
                error?.error?.message || error?.message || 'Unknown error';
            alert(`Operation failed: ${errorMsg}`);
            this.loading.set(false);
        } finally {
            this.loading.set(false);
        }
    }

    async selectAndUploadPdfs(
        organizationSiteKey: string,
        bundleKey: string,
        initialMessage: string = ''
    ): Promise<void> {
        let files: FileList | null = null;
        try {
            files = await this.promptForFiles('.pdf', true);
        } catch (promptError: any) {
            alert(
                `Could not open file selection dialog: ${promptError.message}`
            );
            this.loading.set(false);
            return;
        }

        if (!files || files.length === 0) {
            alert(initialMessage + 'No files selected for upload.');
            this.loading.set(false);
            return;
        }

        try {
            const uploadObservables: Observable<any>[] = [];
            const fileArray = Array.from(files);

            for (const file of fileArray) {
                try {
                    const base64String = await this.readFileAsBase64(file);
                    const payload = {
                        orderNumber:
                            'ORD-' +
                            Math.floor(
                                10000 + Math.random() * 90000
                            ).toString(),
                        dateAnnounced: new Date().getTime(),
                        number:
                            'DN-' +
                            Math.floor(
                                10000 + Math.random() * 90000
                            ).toString(),
                        originalConsignor: 'Mock Consignor Company',
                        referenceNumber: 'Mock Consignor Referencenumber',
                        originalConsignorGln: '1234567890123',
                    };

                    const upload$ = this.deliveryNotesService
                        .uploadDeliveryNote(
                            organizationSiteKey,
                            file.name,
                            base64String,
                            bundleKey,
                            payload.orderNumber,
                            payload.dateAnnounced,
                            payload.number,
                            payload.originalConsignor,
                            payload.originalConsignorGln,
                            payload.referenceNumber
                        )
                        .pipe(
                            tap((event: HttpEvent<any>) => {}),
                            catchError(err => {
                                const errorMsg =
                                    err?.error?.message ||
                                    err?.message ||
                                    'Unknown upload error';
                                return of({
                                    error: true,
                                    file: file.name,
                                    message: errorMsg,
                                });
                            })
                        );
                    uploadObservables.push(upload$);
                } catch (readError: any) {
                    alert(
                        `Error reading file ${file.name}: ${readError.message}`
                    );
                    uploadObservables.push(
                        of({
                            error: true,
                            file: file.name,
                            message: `Failed to read file: ${readError.message}`,
                        })
                    );
                }
            }

            let finalMessage = initialMessage;

            if (uploadObservables.length > 0) {
                const results = await lastValueFrom(
                    forkJoin(uploadObservables)
                );

                const successfulUploads = results.filter(
                    res => !(res as any)?.error
                );
                const failedUploads = results.filter(
                    res => (res as any)?.error
                );

                let checkoutAttempted = false;
                let checkoutSuccessful = false;
                let checkoutErrorMsg: string | null = null;

                if (successfulUploads.length > 0) {
                    checkoutAttempted = true;
                    try {
                        const driverSignatureImage =
                            'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

                        await lastValueFrom(
                            this.deliveryNoteBundlesService.postDeliveryNoteBundlesCheckout(
                                {
                                    organizationSiteKey: organizationSiteKey,
                                    body: {
                                        deliveryNoteBundleKeys: [bundleKey],
                                        driverSignature: driverSignatureImage,
                                    },
                                }
                            )
                        );
                        checkoutSuccessful = true;
                    } catch (checkoutError: any) {
                        checkoutErrorMsg =
                            checkoutError?.error?.message ||
                            checkoutError?.message ||
                            'Unknown checkout error';
                    }
                }

                if (successfulUploads.length > 0) {
                    finalMessage += `${successfulUploads.length} PDF(s) uploaded successfully.\n`;
                }
                if (failedUploads.length > 0) {
                    finalMessage += `${failedUploads.length} PDF(s) failed to upload:\n`;
                    failedUploads.forEach(fail => {
                        finalMessage += `- ${(fail as any).file}: ${(fail as any).message}\n`;
                    });
                    finalMessage += '\n';
                }
                if (checkoutAttempted) {
                    if (checkoutSuccessful) {
                        finalMessage += `Bundle checkout successful.`;
                    } else {
                        finalMessage += `Bundle checkout FAILED: ${checkoutErrorMsg || 'Unknown reason'}`;
                    }
                } else if (
                    successfulUploads.length === 0 &&
                    failedUploads.length > 0
                ) {
                    finalMessage += `Bundle checkout not attempted due to upload errors.`;
                } else if (
                    successfulUploads.length === 0 &&
                    failedUploads.length === 0
                ) {
                    finalMessage += `No files were processed successfully.`;
                }

                alert(finalMessage.trim());
            } else {
                alert(
                    initialMessage +
                        'No files were processed for upload (check for reading errors).'
                );
            }
        } catch (error: any) {
            alert(
                `Error during PDF processing or checkout: ${error.message || 'Unknown error'}`
            );
        } finally {
            this.loading.set(false);
        }
    }

    private promptForFiles(
        accept: string = '*',
        multiple: boolean = false
    ): Promise<FileList | null> {
        return new Promise((resolve, reject) => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = accept;
            input.multiple = multiple;
            input.style.display = 'none';

            let changeListener: ((event: Event) => void) | null = null;
            let focusListener: (() => void) | null = null;
            let inputClicked = false;

            const cleanup = () => {
                if (changeListener) {
                    input.removeEventListener('change', changeListener);
                }
                if (focusListener) {
                    window.removeEventListener('focus', focusListener);
                }
                if (input.parentNode) {
                    document.body.removeChild(input);
                }
            };

            changeListener = (event: Event) => {
                const target = event.target as HTMLInputElement;
                cleanup();
                resolve(target.files);
            };

            focusListener = () => {
                setTimeout(() => {
                    if (
                        inputClicked &&
                        (!input.files || input.files.length === 0)
                    ) {
                        cleanup();
                        resolve(null);
                    } else if (
                        inputClicked &&
                        input.files &&
                        input.files.length > 0
                    ) {
                        cleanup();
                    }
                }, 200);
            };

            input.addEventListener('change', changeListener, { once: true });
            window.addEventListener('focus', focusListener, { once: true });

            document.body.appendChild(input);
            try {
                inputClicked = true;
                input.click();
            } catch (err: any) {
                inputClicked = false;
                cleanup();
                reject(
                    new Error(
                        `Failed to programmatically click the file input: ${err.message}`
                    )
                );
            }
        });
    }

    private readFileAsBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            if (!file) {
                return reject(
                    new Error('Invalid file object provided to reader.')
                );
            }
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result as string;
                if (
                    result &&
                    result.startsWith('data:') &&
                    result.includes(',')
                ) {
                    const base64Content = result.split(',')[1];
                    resolve(base64Content);
                } else {
                    reject(new Error('Invalid file reader result format.'));
                }
            };
            reader.onerror = error => {
                reject(
                    new Error(
                        `FileReader error: ${error.type || 'Unknown error'}`
                    )
                );
            };
            reader.readAsDataURL(file);
        });
    }

    getEnvironment(): Observable<'production' | 'test'> {
        return this.debugStateSubject.pipe(map(state => state.environment));
    }

    getCurrentEnvironment(): 'production' | 'test' {
        return this.debugStateSubject.value.environment;
    }

    setEnvironment(env: 'production' | 'test'): void {
        this.updateFeature('environment', env === 'test');
    }

    isProduction(): boolean {
        return this.getCurrentEnvironment() === 'production';
    }

    getLoggingState(): Observable<boolean> {
        return this.debugStateSubject.pipe(map(state => state.loggingEnabled));
    }

    isLoggingEnabled(): boolean {
        return this.debugStateSubject.value.loggingEnabled;
    }

    getFeaturesObservable(): Observable<DebugFeature[]> {
        return this.featuresSubject.asObservable();
    }

    getFeatures(): DebugFeature[] {
        return [...this.featuresSubject.value];
    }

    /**
     * Opens a dialog to select all actors (consignor, carrier, consignee) at once
     */
    async openActorSelectionDialog(
        actors: any
    ): Promise<SelectedActors | null> {
        try {
            if (this.isLoggingEnabled()) {
                console.log('[DebugService] Opening actor selection dialog');
            }

            const { ChainSelectionDialogComponent } = await import(
                './../../shared/components/chain-selection-dialog/chain-selection-dialog.component'
            );

            const dialogRef = this.dialog.open(ChainSelectionDialogComponent, {
                width: '90%',
                maxWidth: '1200px',
                disableClose: false,
                data: {
                    actors: actors,
                },
            });

            const result = await lastValueFrom(dialogRef.afterClosed());

            if (this.isLoggingEnabled()) {
                console.log(
                    '[DebugService] Dialog closed with result:',
                    result
                );
            }

            return result;
        } catch (error) {
            console.error('Error opening actor selection dialog:', error);
            return null;
        }
    }

    /**
     * Opens the article compare dialog for comparing article data
     */
    async compareArticle(): Promise<void> {
        try {
            // Check if user is on a /consignee route
            if (!window.location.href.includes('/consignee/checkin-notes')) {
                alert(
                    'Die Funktion "Artikel vergleichen" ist nur beim Check-In eines Lieferscheins verfügbar.'
                );
                if (this.isLoggingEnabled()) {
                    console.log(
                        '[DebugService] compareArticle denied: Not on consignee route. Current URL:',
                        window.location.href
                    );
                }
                return;
            }

            if (this.isLoggingEnabled()) {
                console.log('[DebugService] Opening article compare dialog');
            }

            const organizationSiteKey =
                this.sessionService.currentOrganizationKey;

            const dialogRef = this.dialog.open(
                DebugArticleCompareDialogComponent,
                {
                    width: '90%',
                    maxWidth: '800px',
                    disableClose: false,
                    data: {
                        organizationSiteKey: organizationSiteKey,
                    } as DebugArticleCompareInputData,
                }
            );

            const result = await lastValueFrom(dialogRef.afterClosed());

            if (this.isLoggingEnabled()) {
                console.log(
                    '[DebugService] Article compare dialog result:',
                    result
                );
            }

            if (result && result.result === 'success' && result.data) {
                await this.submitArticleComparison(result.data);
            }
        } catch (error: any) {
            console.error('Error opening article compare dialog:', error);
            alert(
                `Error opening article comparison dialog: ${error?.message || 'Unknown error'}`
            );
        }
    }

    /**
     * Submits the article comparison data to the backend API
     */
    private async submitArticleComparison(
        articlesData: DebugArticleCompareResultData
    ): Promise<void> {
        try {
            if (this.isLoggingEnabled()) {
                console.log(
                    '[DebugService] Calling postConsigneeArticlesCompare with:',
                    articlesData
                );
            }

            // Call the actual API with the array of articles
            await lastValueFrom(
                this.articlesService.postConsigneeArticlesCompare({
                    organizationSiteKey: articlesData.organizationSiteKey,
                    body: articlesData.articles,
                })
            );

            const articleCount = articlesData.articles.length;
            const articlesSummary = articlesData.articles
                .map(
                    (article, index) =>
                        `${index + 1}. PO: ${article.articleOrderNumber}, EmpfängerBN: ${article.consigneeOrderNumber}, Anzahl: ${article.quantity}`
                )
                .join('\n');

            alert(
                `Artikel Daten erfolgreich übermittelt!\n\n` +
                    `${articleCount} Artikel übermittelt:\n${articlesSummary}`
            );

            if (this.isLoggingEnabled()) {
                console.log(
                    '[DebugService] Article comparison submitted successfully'
                );
            }
        } catch (error: any) {
            const errorMsg =
                error?.error?.message || error?.message || 'Unknown error';
            alert(`Article comparison submission failed: ${errorMsg}`);
            if (this.isLoggingEnabled()) {
                console.error(
                    '[DebugService] Article comparison error:',
                    error
                );
            }
        }
    }

    /**
     * Opens the ASN upload dialog for updating advanced shipping notices
     */
    async updateAsn(): Promise<void> {
        try {
            // Check if user is on a /consignee route
            if (!window.location.href.includes('/consignee/checkin-notes')) {
                alert(
                    'Die Funktion "ASN hochladen" ist nur beim Check-In eines Lieferscheins verfügbar.'
                );
                if (this.isLoggingEnabled()) {
                    console.log(
                        '[DebugService] updateAsn denied: Not on consignee route. Current URL:',
                        window.location.href
                    );
                }
                return;
            }
            if (this.isLoggingEnabled()) {
                console.log('[DebugService] Opening ASN update dialog');
            }

            const organizationSiteKey =
                this.sessionService.currentOrganizationKey;

            const dialogRef = this.dialog.open(DebugAsnUploadDialogComponent, {
                width: '90%',
                maxWidth: '1000px',
                disableClose: false,
                data: {
                    organizationSiteKey: organizationSiteKey,
                } as DebugAsnUploadInputData,
            });

            const result = await lastValueFrom(dialogRef.afterClosed());

            if (this.isLoggingEnabled()) {
                console.log('[DebugService] ASN update dialog result:', result);
            }

            if (result) {
                await this.submitAsnUpdate(result);
            }
        } catch (error: any) {
            console.error('Error opening ASN update dialog:', error);
            alert(
                `Error opening ASN update dialog: ${error?.message || 'Unknown error'}`
            );
        }
    }

    /**
     * Submits the ASN update data to the backend API
     */
    private async submitAsnUpdate(
        asnData: DebugAsnUploadResultData
    ): Promise<void> {
        try {
            if (this.isLoggingEnabled()) {
                console.log(
                    '[DebugService] Calling postAdvancedShippingNotesV3 with:',
                    asnData
                );
            }

            // Call the actual API with the array of ASN items
            await lastValueFrom(
                this.advancedShippingNoticeService.postAdvancedShippingNotesV3({
                    organizationSiteKey: asnData.organizationSiteKey,
                    body: asnData.items,
                })
            );

            const itemCount = asnData.items.length;
            const itemsSummary = asnData.items
                .map(
                    (item, index) =>
                        `${index + 1}. ASN: ${item.advancedShippingNotice}, PO: ${item.articleOrderNumber}, Menge: ${item.quantity}`
                )
                .join('\n');

            alert(
                `ASN Daten erfolgreich übermittelt!\n\n` +
                    `${itemCount} ASN Artikel übermittelt:\n${itemsSummary}`
            );

            if (this.isLoggingEnabled()) {
                console.log('[DebugService] ASN update submitted successfully');
            }
        } catch (error: any) {
            const errorMsg =
                error?.error?.message || error?.message || 'Unknown error';
            alert(`ASN update submission failed: ${errorMsg}`);
            if (this.isLoggingEnabled()) {
                console.error('[DebugService] ASN update error:', error);
            }
        }
    }

    /**
     * Opens the manage articles dialog for managing articles in a delivery note
     */
    async manageArticles(): Promise<void> {
        try {
            // Check if user is on a /consignor route for creating/editing a bundle
            if (!window.location.href.includes('/consignor/edit-bundle/')) {
                alert(
                    'Die Funktion "Artikel verwalten" ist nur verfügbar, wenn Sie als Versender eine neue Mappe anlegen oder eine bestehende bearbeiten, die noch nicht ausgecheckt wurde.'
                );
                if (this.isLoggingEnabled()) {
                    console.log(
                        '[DebugService] manageArticles denied: Not on consignor route. Current URL:',
                        window.location.href
                    );
                }
                return;
            }

            if (this.isLoggingEnabled()) {
                console.log('[DebugService] Opening manage articles dialog');
            }

            // Extract bundle key from URL
            // URL pattern: /consignor/edit-bundle/{bundleKey}/...
            const urlSegments = window.location.href.split('/');
            const bundleKeyIndex = urlSegments.indexOf('edit-bundle');
            const bundleKey =
                bundleKeyIndex >= 0 && bundleKeyIndex + 1 < urlSegments.length
                    ? urlSegments[bundleKeyIndex + 1]
                    : undefined;

            if (!bundleKey) {
                alert(
                    'Bundle key not found in URL. Cannot open manage articles dialog.'
                );
                return;
            }

            const dialogRef = this.dialog.open(
                DebugManageArticlesDialogComponent,
                {
                    width: '90%',
                    maxWidth: '1200px',
                    disableClose: false,
                    data: {
                        bundleKey: bundleKey,
                    } as DebugManageArticlesInputData,
                }
            );

            const result = await lastValueFrom(dialogRef.afterClosed());

            if (this.isLoggingEnabled()) {
                console.log(
                    '[DebugService] Manage articles dialog closed with result:',
                    result
                );
            }
        } catch (error: any) {
            console.error('Error opening manage articles dialog:', error);
            alert(
                `Error opening manage articles dialog: ${error?.message || 'Unknown error'}`
            );
        }
    }

    log(...args: any[]): void {
        if (this.isLoggingEnabled()) {
            console.log('[Debug]', ...args);
        }
    }
}
